import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Users, Check, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import BookingModal from '../components/BookingModal';
import { useCurrency } from '../context/CurrencyContext';
import { trackPackageView, trackBookNowClicked } from '../lib/analytics';
import { PayPalBadge } from '../components/PayPalBadge';
import PackageReviews from '../components/PackageReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await axios.get(`${API}/packages/${id}`);
        setPkg(response.data);
        trackPackageView(response.data);
      } catch (error) {
        console.error('Error fetching package:', error);
      }
    };
    fetchPackage();
  }, [id]);

  // Combined gallery: cover image + gallery images, deduped
  const gallery = useMemo(() => {
    if (!pkg) return [];
    const list = [];
    if (pkg.image_url) list.push(pkg.image_url);
    (pkg.images || []).forEach((url) => {
      if (url && !list.includes(url)) list.push(url);
    });
    return list;
  }, [pkg]);

  const goPrev = () => {
    setActiveImageIdx((idx) => (idx - 1 + gallery.length) % gallery.length);
  };

  const goNext = () => {
    setActiveImageIdx((idx) => (idx + 1) % gallery.length);
  };

  if (!pkg) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" data-testid="loading">
        <p className="font-body text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-sand min-h-screen pt-20" data-testid="package-detail-page">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/packages')}
          data-testid="back-button"
          className="flex items-center space-x-2 text-navy hover:text-emerald-600 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-body">Back to Packages</span>
        </button>
      </div>

      {/* Package Header */}
      <section className="px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
              data-testid="package-gallery"
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl relative bg-navy/5">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={gallery[activeImageIdx] || pkg.image_url}
                    src={gallery[activeImageIdx] || pkg.image_url}
                    alt={`${pkg.name} - ${activeImageIdx + 1}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="w-full h-full object-cover"
                  />
                </AnimatePresence>

                {gallery.length > 1 && (
                  <>
                    <button
                      onClick={goPrev}
                      data-testid="gallery-prev"
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-emerald-700 p-2 rounded-full shadow-lg transition"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={22} />
                    </button>
                    <button
                      onClick={goNext}
                      data-testid="gallery-next"
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-emerald-700 p-2 rounded-full shadow-lg transition"
                      aria-label="Next image"
                    >
                      <ChevronRight size={22} />
                    </button>
                    <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1 rounded-full font-body">
                      {activeImageIdx + 1} / {gallery.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {gallery.length > 1 && (
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2" data-testid="gallery-thumbnails">
                  {gallery.map((url, idx) => (
                    <button
                      key={`${url}-${idx}`}
                      onClick={() => setActiveImageIdx(idx)}
                      className={`aspect-square rounded-md overflow-hidden ring-2 transition-all ${
                        idx === activeImageIdx
                          ? 'ring-emerald-600 scale-105 shadow-md'
                          : 'ring-transparent opacity-70 hover:opacity-100'
                      }`}
                      data-testid={`gallery-thumb-${idx}`}
                    >
                      <img src={url} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-col justify-center"
            >
              <span className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {pkg.category}
              </span>
              <h1 className="font-heading text-4xl md:text-6xl font-normal leading-tight text-navy mb-6">
                {pkg.name}
              </h1>
              <p className="font-body text-base md:text-lg text-foreground/80 leading-relaxed mb-8 whitespace-pre-line">
                {pkg.description}
              </p>

              <div className="flex items-center space-x-8 mb-8">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Clock size={24} className="text-emerald-600" />
                  <div>
                    <p className="font-body text-sm">Duration</p>
                    <p className="font-body font-semibold text-navy">{pkg.duration}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Users size={24} className="text-emerald-600" />
                  <div>
                    <p className="font-body text-sm">Max Guests</p>
                    <p className="font-body font-semibold text-navy">Up to {pkg.max_guests}</p>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <p className="price-luxe-bold text-5xl md:text-6xl text-emerald-700 leading-none" data-testid="package-price">
                  {formatPrice(pkg.price)}
                </p>
                <p className="font-body text-sm text-muted-foreground mt-2" data-testid="package-price-suffix">
                  {pkg.pricing_type === 'per_person'
                    ? 'per person'
                    : `total for the group (up to ${pkg.max_guests} guests)`}
                </p>
              </div>

              <button
                onClick={() => { trackBookNowClicked(pkg); setShowBooking(true); }}
                data-testid="book-now-button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-6 rounded-full font-bold text-xl tracking-wide transition-all hover:scale-110 hover:shadow-2xl w-full md:w-auto shadow-lg border-4 border-emerald-400"
                style={{ backgroundColor: '#059669', color: '#ffffff' }}
              >
                <span className="text-white font-bold text-xl">Book This Safari</span>
              </button>

              <div className="mt-4">
                <PayPalBadge variant="compact" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Package Details */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Highlights */}
            <div data-testid="highlights-section">
              <h2 className="font-heading text-2xl md:text-4xl font-normal text-navy mb-6">
                Highlights
              </h2>
              <ul className="space-y-3">
                {(pkg.highlights || []).map((highlight, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Check size={20} className="text-emerald-600 mt-1 flex-shrink-0" />
                    <span className="font-body text-foreground/80">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Inclusions */}
            <div data-testid="inclusions-section">
              <h2 className="font-heading text-2xl md:text-4xl font-normal text-navy mb-6">
                What's Included
              </h2>
              <ul className="space-y-3">
                {(pkg.inclusions || []).map((inclusion, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Check size={20} className="text-gold mt-1 flex-shrink-0" />
                    <span className="font-body text-foreground/80">{inclusion}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Verified Reviews from Guests */}
      <PackageReviews packageId={pkg.id} />

      {/* Safari Guidelines */}
      <section className="py-24 px-4 bg-navy" data-testid="guidelines-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-normal text-gold mb-8 text-center">
            Safari Guidelines
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-sand/10 p-6 rounded-lg">
              <h3 className="font-heading text-xl text-sand mb-3">What to Bring</h3>
              <ul className="space-y-2 font-body text-sm text-sand/80">
                <li>• Comfortable clothing in earth tones</li>
                <li>• Hat, sunglasses, and sunscreen</li>
                <li>• Camera with zoom lens</li>
                <li>• Binoculars (optional)</li>
              </ul>
            </div>
            <div className="bg-sand/10 p-6 rounded-lg">
              <h3 className="font-heading text-xl text-sand mb-3">Important Rules</h3>
              <ul className="space-y-2 font-body text-sm text-sand/80">
                <li>• Maintain silence during safari</li>
                <li>• Do not litter in the reserve</li>
                <li>• Follow guide's instructions</li>
                <li>• No smoking or alcohol allowed</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {showBooking && (
        <BookingModal
          package={pkg}
          onClose={() => setShowBooking(false)}
        />
      )}
    </div>
  );
};

export default PackageDetail;
